/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Stack,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    InputAdornment,
    Fade,
    Container
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAcquittals } from 'hooks/api/travel-acquital/useTravelAcquital';
import { useDepartment } from 'hooks/api/terminology/department/useDepartment';
import { useBranchCode } from 'hooks/api/terminology/branch-code/useBranchCode';
import MainCard from 'ui-component/cards/MainCard';
import { format } from 'date-fns';
import {
    IconSearch,
    IconFilterOff,
    IconFileDescription,
    IconEye,
    IconCalendar,
    IconMail,
    IconId,
    IconFilter,
    IconRefresh,
    IconBuilding,
    IconBuildingCommunity,
    IconUser,
    IconTimeline
} from '@tabler/icons';
import FinanceAcquittalPreviewDialog from './FinanceAcquittalPreviewDialog';
import { AcquittalTimelineDialog } from '../../../trip/AcquittalTimelineDialog';

const FinanceOfficerAcquittals = () => {
    const { getAcquittals, getTimelineStatuses } = useAcquittals();
    const { listDepartment } = useDepartment();
    const { listBranchCodes } = useBranchCode();
    const [acquittals, setAcquittals] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [branchCodes, setBranchCodes] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showFilters, setShowFilters] = useState(true);
    const [selectedAcquittal, setSelectedAcquittal] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [timelineOpen, setTimelineOpen] = useState(false);
    const [selectedTimelineData, setSelectedTimelineData] = useState(null);
    const [timeline, setTimeline] = useState([]);

    // Filter states
    const [filters, setFilters] = useState({
        userEmail: '',
        preparedByEmail: '',
        travelRequestId: '',
        acquittalId: '',
        status: '',
        branchCode: '',
        departmentCode: '',
        createdAtFrom: null,
        createdAtTo: null
    });

    // Pagination states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Load departments and branch codes
    useEffect(() => {
        const loadData = async () => {
            try {
                const [departmentsResponse, branchCodesResponse] = await Promise.all([
                    listDepartment(0, 1000),
                    listBranchCodes(0, 1000)
                ]);
                setDepartments(departmentsResponse.content || []);
                setBranchCodes(branchCodesResponse.content || []);
            } catch (error) {
                console.error('Error loading reference data:', error);
            }
        };
        loadData();
    }, []);

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (name, value) => {
        setFilters((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSearch = async () => {
        setLoading(true);
        setError(null);
        try {
            const formattedFilters = {
                ...filters,
                createdAtFrom: filters.createdAtFrom ? format(filters.createdAtFrom, "yyyy-MM-dd'T'HH:mm:ss'Z'") : null,
                createdAtTo: filters.createdAtTo ? format(filters.createdAtTo, "yyyy-MM-dd'T'HH:mm:ss'Z'") : null,
                type: 'ALL',
                branchCode: filters.branchCode || null,
                departmentCode: filters.departmentCode || null,
                page,
                size: rowsPerPage
            };

            const response = await getAcquittals(formattedFilters);
            setAcquittals(response.content);
            setTotalElements(response.totalElements);
        } catch (error) {
            console.error('Error fetching acquittals:', error);
            setError(error.message || 'Error fetching acquittals');
        } finally {
            setLoading(false);
        }
    };

    const handleClearFilters = () => {
        setFilters({
            userEmail: '',
            preparedByEmail: '',
            travelRequestId: '',
            acquittalId: '',
            status: '',
            branchCode: '',
            departmentCode: '',
            createdAtFrom: null,
            createdAtTo: null
        });
        setPage(0);
        handleSearch();
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        handleSearch();
    }, [page, rowsPerPage]);

    const statusOptions = [
        { value: '', label: 'All' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'DECLINED', label: 'Declined' },
        { value: 'PENDING', label: 'Pending' },
        { value: 'SELF_DECLINED', label: 'Self Declined' },
        { value: 'COMPLETED', label: 'Completed' }
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'APPROVED':
                return 'success';
            case 'DECLINED':
            case 'SELF_DECLINED':
                return 'error';
            case 'PENDING':
                return 'warning';
            case 'COMPLETED':
                return 'info';
            default:
                return 'default';
        }
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        if (filters.userEmail) count++;
        if (filters.preparedByEmail) count++;
        if (filters.travelRequestId) count++;
        if (filters.acquittalId) count++;
        if (filters.status) count++;
        if (filters.branchCode) count++;
        if (filters.departmentCode) count++;
        return count;
    };

    const handlePreviewOpen = (acquittal) => {
        setSelectedAcquittal(acquittal);
        setIsPreviewOpen(true);
    };

    const handlePreviewClose = () => {
        setSelectedAcquittal(null);
        setIsPreviewOpen(false);
    };

    const handleTimelineView = async (acquittal) => {
        try {
            setSelectedTimelineData(acquittal);
            const timelineData = await getTimelineStatuses(acquittal.id);
            setTimeline(timelineData);
            setTimelineOpen(true);
        } catch (error) {
            console.error('Error fetching timeline:', error);
            Swal.fire('Error', 'Failed to load timeline', 'error');
        }
    };

    if (error) {
        return (
            <Container maxWidth="xl">
                <MainCard>
                    <Alert
                        severity="error"
                        sx={{
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: '1.5rem'
                            }
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Error Loading Acquittals
                        </Typography>
                        {error}
                    </Alert>
                </MainCard>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <MainCard sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
                {/* Filters Section */}
                <Fade in={showFilters}>
                    <Card sx={{
                        mb: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconFilter size={20} />
                                Filter Options
                                {getActiveFiltersCount() > 0 && (
                                    <Chip
                                        label={`${getActiveFiltersCount()} active`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2}>
                                {/* Date Range Filters */}
                                <Grid item xs={12} md={6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DateTimePicker
                                            label="From Date & Time"
                                            value={filters.createdAtFrom}
                                            onChange={(value) => handleDateChange('createdAtFrom', value)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    size="small"
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                />
                                            )}
                                            ampm={false}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DateTimePicker
                                            label="To Date & Time"
                                            value={filters.createdAtTo}
                                            onChange={(value) => handleDateChange('createdAtTo', value)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    size="small"
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                />
                                            )}
                                            ampm={false}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                {/* IDs and Status */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Acquittal ID"
                                        name="acquittalId"
                                        value={filters.acquittalId}
                                        onChange={handleFilterChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <IconId size={18} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={filters.status}
                                            onChange={handleFilterChange}
                                            label="Status"
                                            sx={{ borderRadius: 2 }}
                                        >
                                            {statusOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* User Information */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="User Email"
                                        name="userEmail"
                                        value={filters.userEmail}
                                        onChange={handleFilterChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <IconMail size={18} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Prepared By Email"
                                        name="preparedByEmail"
                                        value={filters.preparedByEmail}
                                        onChange={handleFilterChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <IconMail size={18} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>

                                {/* Organization Details */}
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Branch Code</InputLabel>
                                        <Select
                                            name="branchCode"
                                            value={filters.branchCode}
                                            onChange={handleFilterChange}
                                            label="Branch Code"
                                            sx={{ borderRadius: 2 }}
                                        >
                                            <MenuItem value="">All Branch Codes</MenuItem>
                                            {branchCodes.map((option) => (
                                                <MenuItem key={option.id} value={option.code}>
                                                    {option.code}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Department</InputLabel>
                                        <Select
                                            name="departmentCode"
                                            value={filters.departmentCode}
                                            onChange={handleFilterChange}
                                            label="Department"
                                            sx={{ borderRadius: 2 }}
                                        >
                                            <MenuItem value="">All Departments</MenuItem>
                                            {departments.map((option) => (
                                                <MenuItem key={option.id} value={option.groupCode}>
                                                    {option.groupCode}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Action Buttons */}
                                <Grid item xs={12}>
                                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSearch}
                                            startIcon={<IconSearch size={18} />}
                                            disabled={loading}
                                            sx={{
                                                borderRadius: 2,
                                                py: 1,
                                                px: 3,
                                                fontWeight: 600,
                                            }}
                                        >
                                            Search
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={handleClearFilters}
                                            startIcon={<IconFilterOff size={18} />}
                                            sx={{
                                                borderRadius: 2,
                                                py: 1,
                                                px: 3,
                                                fontWeight: 600,
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Fade>

                {/* Results Section */}
                <Card sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                }}>
                    <Box sx={{
                        backgroundColor: 'grey.50',
                        px: 3,
                        py: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Results ({totalElements} total)
                            </Typography>
                            {loading && (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CircularProgress size={16} />
                                    <Typography variant="body2" color="textSecondary">
                                        Loading...
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    </Box>

                    <TableContainer sx={{ position: 'relative', minHeight: 400 }}>
                        {loading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    zIndex: 2,
                                    backdropFilter: 'blur(2px)'
                                }}
                            >
                                <Stack alignItems="center" spacing={2}>
                                    <CircularProgress size={40} />
                                    <Typography variant="body1" color="textSecondary">
                                        Fetching acquittals...
                                    </Typography>
                                </Stack>
                            </Box>
                        )}

                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: '#1976d2',
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        py: 2
                                    }
                                }}>
                                    <TableCell>Acquittal ID</TableCell>
                                    <TableCell>User Email</TableCell>
                                    <TableCell>Prepared By</TableCell>
                                    <TableCell>Branch</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Created At</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!loading && acquittals.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                            <Stack alignItems="center" spacing={2}>
                                                <IconFileDescription size={48} color="#ccc" />
                                                <Typography variant="h6" color="textSecondary">
                                                    No acquittals found
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Try adjusting your filters or search criteria
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    acquittals.map((acquittal) => (
                                        <TableRow
                                            key={acquittal.id}
                                            sx={{
                                                '&:nth-of-type(even)': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                    transition: 'all 0.2s ease-in-out',
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                },
                                                transition: 'all 0.2s ease-in-out'
                                            }}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    #{acquittal.id}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2">
                                                    {acquittal.user?.email || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {acquittal.preparedBy?.email || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={acquittal.branchCode?.code || 'N/A'}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ borderRadius: 1 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={acquittal.department?.groupCode || 'N/A'}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ borderRadius: 1 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={acquittal.status}
                                                    color={getStatusColor(acquittal.status)}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: 1,
                                                        fontWeight: 600,
                                                        minWidth: 80
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {format(new Date(acquittal.createdAt), 'dd MMM yyyy HH:mm')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handlePreviewOpen(acquittal)}
                                                            sx={{
                                                                backgroundColor: 'primary.main',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    backgroundColor: 'primary.dark',
                                                                    transform: 'scale(1.1)'
                                                                },
                                                                transition: 'all 0.2s ease-in-out'
                                                            }}
                                                        >
                                                            <IconEye size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="View Timeline">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleTimelineView(acquittal)}
                                                            sx={{
                                                                backgroundColor: 'secondary.main',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    backgroundColor: 'secondary.dark',
                                                                    transform: 'scale(1.1)'
                                                                },
                                                                transition: 'all 0.2s ease-in-out'
                                                            }}
                                                        >
                                                            <IconTimeline size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>

                        <TablePagination
                            component="div"
                            count={totalElements}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'grey.50',
                                '& .MuiTablePagination-toolbar': {
                                    minHeight: 64,
                                    px: 3
                                },
                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                    margin: 0,
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                }
                            }}
                        />
                    </TableContainer>
                </Card>
            </MainCard>

            {/* Preview Dialog */}
            <FinanceAcquittalPreviewDialog
                open={isPreviewOpen}
                onClose={handlePreviewClose}
                data={selectedAcquittal}
            />

            {/* Add Timeline Dialog */}
            <AcquittalTimelineDialog
                open={timelineOpen}
                handleClose={() => {
                    setTimelineOpen(false);
                    setSelectedTimelineData(null);
                }}
                timeline={timeline}
            />
        </Container>
    );
};

export default FinanceOfficerAcquittals; 